import { createTheme } from "@mui/material/styles";

const PRIMARY = "#6db43d";
const PRIMARY_LIGHT = "#d0f0bb";
const PRIMARY_DARK = "#4f8c25";
const SECONDARY = "#f27c1e";
const SECONDARY_LIGHT = "#f9caa5";
const SOFT = "#f7fff4";

const theme = createTheme({
  palette: {
    primary: {
      main: PRIMARY,
      light: "#a3d97e",
      dark: PRIMARY_DARK,
    },
    secondary: {
      main: SECONDARY,
      dark: "#c75a06",
    },
    error: {
      main: "#333333",
      light: "#555555",
      dark: "#000000",
      contrastText: "#ffffff",
    },
    warning: {
      main: SECONDARY,
      light: SECONDARY_LIGHT,
      dark: "#c75a06",
      contrastText: "#ffffff",
    },
    info: {
      main: "#666666",
      light: "#999999",
      dark: "#333333",
      contrastText: "#ffffff",
    },
    success: {
      main: PRIMARY,
      light: PRIMARY_LIGHT,
      dark: PRIMARY_DARK,
      contrastText: "#ffffff",
    },
    background: {
      default: "#f9fafc",
      paper: "#ffffff",
    },
    text: {
      primary: "#333",
      secondary: "#666",
    },
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: "Roboto, sans-serif",
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: "#f9fafc",
        },
        "::-webkit-scrollbar": { width: 8, height: 8 },
        "::-webkit-scrollbar-track": {
          background: "rgba(109, 180, 61, 0.08)",
          borderRadius: 4,
        },
        "::-webkit-scrollbar-thumb": {
          background: "linear-gradient(135deg, #6db43d 0%, #5a9e2f 100%)",
          borderRadius: 4,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 2px 12px rgba(33, 33, 33, 0.06)",
          transition: "box-shadow .25s ease, transform .25s ease",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 10 },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "capitalize",
          fontWeight: "bold",
          borderRadius: 20,
          paddingLeft: 24,
          paddingRight: 24,
          paddingTop: 9.6,
          paddingBottom: 9.6,
          transition: "all 0.3s ease",
          "&:active": { transform: "scale(0.97)" },
        },
        containedPrimary: {
          color: "#fff",
          backgroundColor: PRIMARY,
          boxShadow: "0 3px 12px rgba(109, 180, 61, 0.25)",
          "&:hover": {
            color: PRIMARY,
            backgroundColor: PRIMARY_LIGHT,
            transform: "scale(1.05)",
            boxShadow: "0 6px 18px rgba(109, 180, 61, 0.35)",
          },
          "&.Mui-disabled": {
            color: PRIMARY,
            backgroundColor: SOFT,
          },
        },
        containedSecondary: {
          color: "#fff",
          backgroundColor: SECONDARY,
          boxShadow: "0 3px 12px rgba(242, 124, 30, 0.25)",
          "&:hover": {
            color: SECONDARY,
            backgroundColor: SECONDARY_LIGHT,
            transform: "scale(1.05)",
            boxShadow: "0 6px 18px rgba(242, 124, 30, 0.35)",
          },
        },
        containedError: {
          "&:hover": { transform: "scale(1.05)" },
        },
        outlinedPrimary: {
          borderColor: PRIMARY,
          "&:hover": {
            backgroundColor: SOFT,
            borderColor: PRIMARY_DARK,
            transform: "scale(1.03)",
          },
        },
        textPrimary: {
          "&:hover": {
            backgroundColor: "rgba(109, 180, 61, 0.08)",
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: "all 0.2s ease",
          "&:hover": { transform: "scale(1.08)" },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: { color: PRIMARY },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          background: "linear-gradient(145deg, #ffffff 0%, #f8fff4 100%)",
          boxShadow: "0 8px 24px rgba(109, 180, 61, 0.15)",
          overflow: "hidden",
          transition: "box-shadow 0.3s ease",
          "&.MuiPaper-outlined": {
            border: "2px solid rgba(109, 180, 61, 0.3)",
          },
          "&:hover": {
            boxShadow: "0 10px 28px rgba(109, 180, 61, 0.22)",
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          background: "linear-gradient(135deg, #6db43d 0%, #5a9e2f 100%)",
          "& .MuiTableCell-head": {
            color: "#ffffff",
            fontWeight: 700,
            fontSize: "0.875rem",
            letterSpacing: "0.01em",
            borderBottom: "2px solid #4f8c25",
            backgroundColor: "transparent",
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: "background-color .15s ease",
          "&:nth-of-type(even):not(.MuiTableRow-head)": {
            backgroundColor: "rgba(109, 180, 61, 0.045)",
          },
          "&.MuiTableRow-hover:hover": {
            backgroundColor: "rgba(109, 180, 61, 0.10)",
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          width: "100%",
          tableLayout: "auto",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: "1px solid rgba(109, 180, 61, 0.12)",
          textAlign: "center",
          verticalAlign: "middle",
        },
        head: {
          textAlign: "center",
        },
        body: {
          color: "#2d5016",
          textAlign: "center",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500 },
      },
    },
  },
});

export default theme;
